import React, { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { Box, IconButton, Chip, TextField, Button, Typography, Collapse } from '@mui/material'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import { useTheme } from '@mui/material/styles'
import { setJobFeedback, submitJobFeedback } from '/src/redux/aiJobSearch'

const JOB_FEEDBACK_REASONS = [
  { key: 'inaccurate_match', label: 'Inaccurate match' },
  { key: 'wrong_specialty', label: 'Wrong specialty' },
  { key: 'wrong_location', label: 'Wrong location' },
  { key: 'salary_mismatch', label: 'Salary mismatch' },
  { key: 'missing_requirements', label: 'Missing requirements' },
  { key: 'justification_unclear', label: 'Justification unclear' },
  { key: 'other', label: 'Other' },
]

const POSITIVE_JOB_FEEDBACK_REASONS = [
  { key: 'accurate_match', label: 'Match is accurate' },
  { key: 'good_specialty_fit', label: 'Good specialty fit' },
  { key: 'location_right', label: 'Location is right' },
  { key: 'salary_meets_expectations', label: 'Salary meets expectations' },
  { key: 'criteria_match', label: 'Job criteria match' },
  { key: 'other', label: 'Other' },
]

const getMatchTierColor = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent':
      return {
        bg: theme.palette.success.light,
        color: theme.palette.success.dark,
        border: theme.palette.success.main,
      }
    case 'strong':
      return {
        bg: theme.palette.info.light,
        color: theme.palette.info.dark,
        border: theme.palette.info.main,
      }
    case 'good':
      return {
        bg: theme.palette.warning.light,
        color: theme.palette.warning.dark,
        border: theme.palette.warning.main,
      }
    case 'moderate':
    default:
      return {
        bg: theme.palette.grey[100],
        color: theme.palette.grey[700],
        border: theme.palette.grey[400],
      }
  }
}

const useJobFeedback = ({ jobId, conversationId, chatbotMessageId, matchTier, feedback }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const [selectedReasons, setSelectedReasons] = useState(feedback?.reasons || [])
  const [comment, setComment] = useState(feedback?.comment || '')
  const [showThanks, setShowThanks] = useState(false)

  const thanksTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (thanksTimerRef.current) {
        clearTimeout(thanksTimerRef.current)
      }
    }
  }, [])

  const vote = feedback?.vote || null
  const isSubmitted = !!feedback?.submittedAt
  const canSubmit = selectedReasons.length > 0 || comment.trim().length > 0

  const matchTierColors = getMatchTierColor(matchTier, theme)

  const handleThumbUp = () => {
    if (vote === 'up') {
      // Clicking the same thumb again - just clear local state (don't submit)
      dispatch(setJobFeedback({ jobId, vote: null }))
    } else {
      // New thumbs up - submit vote immediately to backend, keep panel open for detail
      dispatch(setJobFeedback({
        jobId,
        vote: 'up',
        reasons: [],
        comment: '',
      }))
      submitJobFeedback({
        jobId,
        conversationId,
        chatbotMessageId,
        vote: 'up',
        reasons: [],
        comment: '',
      })
      setSelectedReasons([])
      setComment('')
    }
  }

  const handleThumbDown = () => {
    if (vote === 'down') {
      // Clicking the same thumb again - just clear local state (don't submit)
      dispatch(setJobFeedback({ jobId, vote: null }))
      setSelectedReasons([])
      setComment('')
    } else {
      // New thumbs down - submit vote immediately to backend, keep panel open for detail
      dispatch(setJobFeedback({
        jobId,
        vote: 'down',
        reasons: [],
        comment: '',
      }))
      submitJobFeedback({
        jobId,
        conversationId,
        chatbotMessageId,
        vote: 'down',
        reasons: [],
        comment: '',
      })
    }
  }

  const handleReasonToggle = (key) => {
    setSelectedReasons(prev =>
      prev.includes(key) ? prev.filter(r => r !== key) : [...prev, key]
    )
  }

  const handleSubmit = () => {
    // Update local state with the detailed feedback
    dispatch(setJobFeedback({
      jobId,
      vote,
      reasons: selectedReasons,
      comment,
      submittedAt: new Date().toISOString(),
    }))

    // Submit to backend (will upsert existing feedback with reasons/comment)
    submitJobFeedback({
      jobId,
      conversationId,
      chatbotMessageId,
      vote,
      reasons: selectedReasons,
      comment,
    })

    setShowThanks(true)
    thanksTimerRef.current = setTimeout(() => {
      setShowThanks(false)
    }, 2000)
  }

  const thumbs = (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          handleThumbUp()
        }}
        sx={{
          minWidth: { xs: 44, md: 'auto' },
          minHeight: { xs: 44, md: 'auto' },
          color: vote === 'up'
            ? matchTierColors.border
            : (vote === 'down' ? theme.palette.grey[300] : theme.palette.grey[400]),
          '&:hover': {
            color: vote === 'up' ? matchTierColors.border : theme.palette.grey[600],
          },
        }}
      >
        {vote === 'up'
          ? <ThumbUpIcon sx={{ fontSize: { xs: '1.125rem', md: '0.875rem' } }} />
          : <ThumbUpOffAltIcon sx={{ fontSize: { xs: '1.125rem', md: '0.875rem' } }} />
        }
      </IconButton>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation()
          handleThumbDown()
        }}
        sx={{
          minWidth: { xs: 44, md: 'auto' },
          minHeight: { xs: 44, md: 'auto' },
          color: vote === 'down'
            ? matchTierColors.border
            : (vote === 'up' ? theme.palette.grey[300] : theme.palette.grey[400]),
          '&:hover': {
            color: vote === 'down' ? matchTierColors.border : theme.palette.grey[600],
          },
        }}
      >
        {vote === 'down'
          ? <ThumbDownIcon sx={{ fontSize: { xs: '1.125rem', md: '0.875rem' } }} />
          : <ThumbDownOffAltIcon sx={{ fontSize: { xs: '1.125rem', md: '0.875rem' } }} />
        }
      </IconButton>
    </Box>
  )

  const panel = (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Collapse in={vote === 'up' && !isSubmitted} timeout={200}>
        <Box
          sx={{
            mt: 0.5,
            p: 1.5,
            maxWidth: { xs: '90%', sm: '60%' },
            backgroundColor: `${matchTierColors.bg}80`,
            border: `1px solid ${matchTierColors.border}`,
            borderRadius: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.grey[700], fontStyle: 'normal' }}>
            What did you like?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, md: 0.5 }, mt: 1 }}>
            {POSITIVE_JOB_FEEDBACK_REASONS.map(reason => (
              <Chip
                key={reason.key}
                label={reason.label}
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation()
                  handleReasonToggle(reason.key)
                }}
                sx={{
                  cursor: 'pointer',
                  height: { xs: 32, md: 24 },
                  fontSize: { xs: '0.8125rem', md: '0.75rem' },
                  borderColor: selectedReasons.includes(reason.key)
                    ? matchTierColors.border
                    : theme.palette.grey[300],
                  backgroundColor: selectedReasons.includes(reason.key)
                    ? `${matchTierColors.bg}80`
                    : 'transparent',
                  color: selectedReasons.includes(reason.key)
                    ? matchTierColors.color
                    : theme.palette.grey[700],
                  fontWeight: selectedReasons.includes(reason.key) ? 500 : 400,
                  '&:hover': {
                    borderColor: matchTierColors.border,
                    backgroundColor: `${matchTierColors.bg}40`,
                    color: matchTierColors.color,
                  },
                }}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            size="small"
            variant="outlined"
            placeholder="Tell us more (optional)..."
            value={comment}
            onChange={(e) => {
              e.stopPropagation()
              setComment(e.target.value)
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: matchTierColors.border,
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation()
                handleSubmit()
              }}
              disabled={!canSubmit}
              sx={{ color: matchTierColors.border, textTransform: 'none' }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Collapse>

      <Collapse in={vote === 'down' && !isSubmitted} timeout={200}>
        <Box
          sx={{
            mt: 0.5,
            p: 1.5,
            maxWidth: { xs: '90%', sm: '60%' },
            backgroundColor: `${matchTierColors.bg}80`,
            border: `1px solid ${matchTierColors.border}`,
            borderRadius: 1.5,
          }}
        >
          <Typography variant="caption" sx={{ color: theme.palette.grey[700], fontStyle: 'normal' }}>
            What went wrong?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, md: 0.5 }, mt: 1 }}>
            {JOB_FEEDBACK_REASONS.map(reason => (
              <Chip
                key={reason.key}
                label={reason.label}
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation()
                  handleReasonToggle(reason.key)
                }}
                sx={{
                  cursor: 'pointer',
                  height: { xs: 32, md: 24 },
                  fontSize: { xs: '0.8125rem', md: '0.75rem' },
                  borderColor: selectedReasons.includes(reason.key)
                    ? matchTierColors.border
                    : theme.palette.grey[300],
                  backgroundColor: selectedReasons.includes(reason.key)
                    ? `${matchTierColors.bg}80`
                    : 'transparent',
                  color: selectedReasons.includes(reason.key)
                    ? matchTierColors.color
                    : theme.palette.grey[700],
                  fontWeight: selectedReasons.includes(reason.key) ? 500 : 400,
                  '&:hover': {
                    borderColor: matchTierColors.border,
                    backgroundColor: `${matchTierColors.bg}40`,
                    color: matchTierColors.color,
                  },
                }}
              />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            maxRows={3}
            size="small"
            variant="outlined"
            placeholder="Tell us more (optional)..."
            value={comment}
            onChange={(e) => {
              e.stopPropagation()
              setComment(e.target.value)
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: matchTierColors.border,
                },
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size="small"
              variant="text"
              onClick={(e) => {
                e.stopPropagation()
                handleSubmit()
              }}
              disabled={!canSubmit}
              sx={{ color: matchTierColors.border, textTransform: 'none' }}
            >
              Submit
            </Button>
          </Box>
        </Box>
      </Collapse>

      {showThanks && (
        <Typography variant="caption" sx={{ color: matchTierColors.color, mt: 0.5, fontStyle: 'normal' }}>
          Thanks for your feedback
        </Typography>
      )}
    </Box>
  )

  return { thumbs, panel }
}

export default useJobFeedback
